/*
 * Copyright 2018-2024 CommScope, Inc., All rights reserved.
 *
 * This program is confidential and proprietary to CommScope, Inc. (CommScope), and
 * may not be copied, reproduced, modified, disclosed to others, published or used, in
 * whole or in part, without the express prior written permission of CommScope.
 */

import {
  ChartFlexContainer,
  ChartLabel,
  ChartLegend,
  ChartModeSwitcher
} from 'app/components/charts';
import { chartMargins } from 'app/components/charts/constants';
import {
  BlockContainer,
  NoDataToShow,
  TitleBar
} from 'app/components/elements';
import { LoadingIconPlaceholder } from 'app/components/icons';
import { chartModeSuffix } from 'app/constants';
import {
  xAxisLabel,
  xAxisTick
} from 'app/modules/overview/components/client-os/constants';
import { PieBarChartWrapper } from 'app/modules/overview/components/common';
import { getDisplayModeProperties } from 'app/modules/overview/utils';
import {
  incidentByDurationDataSelector,
  incidentDetailsFetchStateSelector
} from 'app/redux/incidents';
import { fetchStatePropTypes } from 'app/redux/utils';
import {
  applyLegendToggles,
  calculateChartHeight,
  createColourFactory
} from 'app/utils';
import classNames from 'classnames';
import { isArray, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { createSelector } from 'reselect';

const getFillColour = createColourFactory(60);
const modeKey = `activePrivate${chartModeSuffix}`;

export class ChartByDuration extends Component {
  constructor(props) {
    super(props);
    this.state = { toggled: [], chartToggle: 'pie' };
  }

  handleChangeToggled = label => {
    const { toggled } = this.state;

    if (toggled.includes(label)) {
      toggled.splice(toggled.indexOf(label), 1);
    } else {
      toggled.push(label);
    }
    this.setState({ toggled });
  };

  setMode = mode => {
    this.setState(prevState => ({
      ...prevState,
      chartToggle: mode,
      key: modeKey
    }));
  };

  render = () => {
    const { chartData, fetchState } = this.props;
    const { toggled, chartToggle } = this.state;

    const { pending, complete, timeout } = fetchState;

    const hasData = isArray(chartData) && !isEmpty(chartData);
    const noDataToShow = !pending && complete && !hasData;
    const dataToRender = timeout || pending ? [] : chartData;

    const { chartHeight, containerHeight } = calculateChartHeight(
      dataToRender.length,
      400,
      150
    );

    let pieData;
    if (chartToggle === 'pie') {
      pieData = applyLegendToggles(dataToRender, toggled, 'duration', 'count');
    }

    const {
      padding: containerPadding,
      classes: wrapperClasses
    } = getDisplayModeProperties(chartToggle, true);

    return (
      <BlockContainer>
        <TitleBar leftChildren="By Duration" />
        <Fragment>
          {noDataToShow ? <NoDataToShow /> : null}
          <Fragment>
            <div
              className={classNames(
                `pb-${containerPadding}`,
                pending || noDataToShow ? 'fetch-state-pending' : ''
              )}
              // style={heightStyle}
            >
              {!isEmpty(dataToRender) && (
                <Fragment>
                  <ChartModeSwitcher
                    onClick={this.setMode}
                    mode={chartToggle}
                  />
                  <PieBarChartWrapper classes={wrapperClasses}>
                    {chartToggle === 'bar' ? (
                      <ChartFlexContainer width="95%" height={containerHeight}>
                        <ComposedChart
                          margin={chartMargins}
                          height={chartHeight}
                          data={chartData}
                          layout="vertical"
                        >
                          <CartesianGrid vertical={true} horizontal={false} />
                          <XAxis
                            type="number"
                            label={{ ...xAxisLabel, value: 'Duration' }}
                            allowDecimals={false}
                            tick={xAxisTick}
                            tickMargin={1}
                          />
                          <YAxis
                            type="category"
                            yAxisId="count"
                            dataKey="duration"
                            stroke="rgb(31, 119, 180)"
                            tick={null}
                          />
                          <Tooltip />
                          <Bar
                            dataKey="count"
                            nameKey="duration"
                            yAxisId="count"
                            dot={false}
                            type="monotone"
                            strokeWidth={1.5}
                          >
                            <LabelList
                              content={<ChartLabel />}
                              dataKey="duration"
                            />
                            {chartData.map(({ duration }) => (
                              <Cell
                                key={duration}
                                fill={getFillColour(duration)}
                              />
                            ))}
                          </Bar>
                        </ComposedChart>
                      </ChartFlexContainer>
                    ) : (
                      <Fragment>
                        <PieChart width={350} height={275}>
                          <Pie
                            data={pieData}
                            dataKey="count"
                            innerRadius="50%"
                            nameKey="duration"
                            outerRadius="100%"
                          >
                            <LabelList
                              content={<ChartLabel />}
                              dataKey="duration"
                            />
                            {chartData.map(({ duration }) => (
                              <Cell
                                key={duration}
                                fill={getFillColour(duration)}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                        <ChartLegend
                          data={chartData}
                          getFillColour={getFillColour}
                          onToggle={this.handleChangeToggled}
                          textProperty="duration"
                          toggled={toggled}
                          countProperty="count"
                          percentProperty="percentage"
                          titleLabel="Duration"
                          textWidth={75}
                        />
                      </Fragment>
                    )}
                  </PieBarChartWrapper>
                </Fragment>
              )}
            </div>
            {pending && <LoadingIconPlaceholder />}
          </Fragment>
        </Fragment>
      </BlockContainer>
    );
  };
}

ChartByDuration.propTypes = {
  chartData: PropTypes.array,
  fetchState: fetchStatePropTypes
};

const mapStateToProps = createSelector(
  incidentByDurationDataSelector,
  incidentDetailsFetchStateSelector,
  (chartData, fetchState) => ({
    chartData,
    fetchState
  })
);

export default connect(mapStateToProps)(ChartByDuration);
